import { injectable } from 'inversify'

import { TOKEN_STORAGE_KEY } from '../constants'

import { LocalStorageProvider } from '#Common/support'

/**
 * Session token persistence for Mini App (memory via Pinia + localStorage).
 * API auth is Authorization Bearer — not cookie session.
 * Legacy cookie `_skuby-web-token` is cleared on read/write/reset.
 */
@injectable()
export class AuthTokenStorage {
  public token: {
    get(): string | null
    set(val: string | null): void
  }

  private readonly storage = new LocalStorageProvider()

  constructor() {
    this.token = {
      get: () => this.readToken(),
      set: val => this.writeToken(val),
    }
  }

  private readToken(): string | null {
    const stored = this.storage.get(TOKEN_STORAGE_KEY)
    if (typeof stored === 'string' && stored.length > 0) {
      this.clearLegacyCookie()
      return stored
    }

    const legacy = this.readLegacyCookie()
    if (legacy) {
      this.storage.set(TOKEN_STORAGE_KEY, legacy)
      this.clearLegacyCookie()
      return legacy
    }

    return null
  }

  private writeToken(val: string | null): void {
    if (val) {
      this.storage.set(TOKEN_STORAGE_KEY, val)
    }
    else if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY)
    }
    this.clearLegacyCookie()
  }

  private readLegacyCookie(): string | null {
    if (typeof document === 'undefined') {
      return null
    }
    const match = document.cookie
      .split(';')
      .map(part => part.trim())
      .find(part => part.startsWith(`${TOKEN_STORAGE_KEY}=`))
    if (!match) {
      return null
    }
    const raw = match.slice(TOKEN_STORAGE_KEY.length + 1)
    try {
      return decodeURIComponent(raw) || null
    }
    catch {
      return raw || null
    }
  }

  private clearLegacyCookie(): void {
    if (typeof document === 'undefined') {
      return
    }
    document.cookie = `${TOKEN_STORAGE_KEY}=; Path=/; Max-Age=0; SameSite=Lax`
  }
}
